import {
  ApplicantReferee,
  ApplicantRefereeRelationshipTag,
} from "@prisma/client";

import { prisma } from "@/lib/prisma";
import { parseProseMirrorSchema } from "@/lib/proseMirror";
import { RelationshipTag } from "@/lib/referees";
import {
  DEFAULT_CLASSIFICATION,
  DEFAULT_REFEREE_TYPE_SLUG,
  mapEnglishVariantToN8n,
  mapLetterNumberToN8n,
  mapTargetCriteriaToN8n,
  mapTonePreferenceToN8n,
  N8nRefLetterPayload,
  validateN8nRefLetterPayload,
} from "@/lib/referees/n8nRefLetterPayload";
import { RefLetterGenerationOptions } from "@/lib/referees/refLetterOptions";

import { getApplicationById } from "@/core/server";

import { parseRefereeContactDetails } from "./parseRefereeContactDetails";

const RELATIONSHIP_CAPACITY_BY_TAG: Record<number, string> = {
  [RelationshipTag.SkipManager]: "Skip Manager",
  [RelationshipTag.DirectManager]: "Direct Manager",
  [RelationshipTag.CLevel]: "C-Level",
  [RelationshipTag.Colleague]: "Colleague",
  [RelationshipTag.FriendAcquaintance]: "Friend / Acquaintance",
  [RelationshipTag.Client]: "Client",
  [RelationshipTag.Investor]: "Investor",
};

function toPlainText(value: string | null | undefined): string {
  if (!value) return "";
  return parseProseMirrorSchema(value).trim();
}

function resolveRelationshipCapacity(
  referee: ApplicantReferee & {
    relationshipTag?: ApplicantRefereeRelationshipTag | null;
  }
): string {
  if (referee.relationshipTag?.name) {
    return referee.relationshipTag.name;
  }

  if (
    referee.relationshipTagId &&
    RELATIONSHIP_CAPACITY_BY_TAG[referee.relationshipTagId]
  ) {
    return RELATIONSHIP_CAPACITY_BY_TAG[referee.relationshipTagId];
  }

  return referee.workedTogether ? "Direct Manager" : "Professional Contact";
}

function resolveSelectClient(applicant: {
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
}): string {
  if (applicant.fullName?.trim()) {
    return applicant.fullName.trim();
  }

  return `${applicant.firstName ?? ""} ${applicant.lastName ?? ""}`.trim();
}

function resolvePlatformCallbackUrl(
  caseworkerEmail?: string
): string | undefined {
  const explicitUrl = process.env.AI_REF_LETTER_WEBHOOK_URL?.trim();
  const appUrl = process.env.NEXT_PUBLIC_APP_URL?.trim().replace(/\/$/, "");
  const apiUrl = process.env.NEXT_PUBLIC_API_URL?.trim().replace(/\/$/, "");

  let baseUrl: string | undefined;
  if (explicitUrl) {
    baseUrl = explicitUrl;
  } else if (appUrl) {
    baseUrl = `${appUrl}/api/webhook/ai-ref-letter-ready`;
  } else if (apiUrl) {
    const origin = apiUrl.endsWith("/api") ? apiUrl.slice(0, -4) : apiUrl;
    baseUrl = `${origin}/api/webhook/ai-ref-letter-ready`;
  }

  if (!baseUrl) return undefined;

  const email = caseworkerEmail?.trim();
  if (!email) return baseUrl;

  try {
    const url = new URL(baseUrl);
    url.searchParams.set("caseworkerEmail", email);
    return url.toString();
  } catch {
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}caseworkerEmail=${encodeURIComponent(email)}`;
  }
}

function resolveScoringResult(referee: ApplicantReferee): number | null {
  if (referee.scoringResultEdited !== null) {
    return referee.scoringResultEdited;
  }
  return referee.scoringResult ?? null;
}

export type BuildN8nRefLetterPayloadParams = {
  applicationId: string;
  refereeId: string;
  caseworkerEmail: string;
  generationOptions?: RefLetterGenerationOptions;
};

export async function buildN8nRefLetterPayload({
  applicationId,
  refereeId,
  caseworkerEmail,
  generationOptions,
}: BuildN8nRefLetterPayloadParams): Promise<N8nRefLetterPayload> {
  const application = await getApplicationById(applicationId);
  if (!application?.applicant) {
    throw new Error("Application not found");
  }

  const referee = await prisma.applicantReferee.findFirst({
    where: {
      id: refereeId,
      applicantId: application.applicant.id,
      deleted: null,
    },
    include: {
      relationshipTag: true,
    },
  });

  if (!referee) {
    throw new Error("Referee not found");
  }

  const { email, phone } = parseRefereeContactDetails(referee.contactDetails);
  const personDesc = toPlainText(referee.personDescription);
  const assessmentDesc = toPlainText(referee.assessmentDescription);
  const meetDesc = toPlainText(referee.meetDescription);
  const conclusion = toPlainText(referee.conclusion);
  const meetActivities = toPlainText(referee.meetAndActivities);
  const jointProject = toPlainText(referee.jointProject);
  const possibleWorks = toPlainText(referee.possibleWorksDescription);
  const jointExternal = (referee.jointExternalProfileActivity ?? []).join(", ");

  const platformCallbackUrl = resolvePlatformCallbackUrl(caseworkerEmail);

  const payload: N8nRefLetterPayload = {
    applicationId,
    ...(platformCallbackUrl && { platformCallbackUrl }),
    selectClient: resolveSelectClient(application.applicant),
    classification: DEFAULT_CLASSIFICATION,
    letterNumber: mapLetterNumberToN8n(
      generationOptions?.refLetterNumber ?? referee.refLetterNumber
    ),
    caseworkerEmail: caseworkerEmail.trim(),
    refereeId: referee.id,
    refereeFullName: `${referee.firstName ?? ""} ${
      referee.lastName ?? ""
    }`.trim(),
    refereeFirstName: referee.firstName ?? "",
    refereeLastName: referee.lastName ?? "",
    refereeEmail: email ?? "",
    refereePhone: phone ?? "",
    refereeBackground: personDesc,
    refereeTypeSlug: DEFAULT_REFEREE_TYPE_SLUG,
    relationshipCapacity: resolveRelationshipCapacity(referee),
    howLongKnown: referee.howLongKnowYou ?? "",
    refereeRawInput: assessmentDesc,
    refereeCompanyNow: referee.companyNameNow ?? "",
    refereeCompanyTogether: referee.companyNameWorkedTogether ?? "",
    refereeWorkedTogether: referee.workedTogether ? "true" : "false",
    refereeLinkedIn: referee.linkedInUrl ?? "",
    refereeHowLongKnowYou: referee.howLongKnowYou ?? "",
    refereeAssessment: assessmentDesc,
    refereeMeetDesc: meetDesc,
    refereePersonDesc: personDesc,
    refereeConclusion: conclusion,
    refereeMeetActivities: meetActivities,
    refereeJointProject: jointProject,
    refereeJointExternal: jointExternal,
    refereePossibleWorks: possibleWorks,
    refereeScoringResult: resolveScoringResult(referee),
    targetCriteria: mapTargetCriteriaToN8n(
      generationOptions?.refLetterTargetCriteria ??
        referee.refLetterTargetCriteria
    ),
    englishVariant: mapEnglishVariantToN8n(
      generationOptions?.refLetterEnglishVariant ??
        referee.refLetterEnglishVariant
    ),
    tonePreference: mapTonePreferenceToN8n(
      generationOptions?.refLetterTonePreference ??
        referee.refLetterTonePreference
    ),
    additionalContext: "",
  };

  validateN8nRefLetterPayload(payload);

  return payload;
}
